'use client'
import { buttonCls } from '@qr-front/ui/components/Button'
import { cn, getVersionForClone } from '@qr-front/ui/utils'
import { useFrameFontSize } from 'features/generator/services/client'
import { useAtomValue } from 'jotai'
import NextImage from 'next/image'
import { useTranslations } from 'next-intl'
import { useEffect, useState } from 'react'

import { addMissingUrlProtocol } from '../../../../../utils/urlUtils'
import {
	appButtonListAtom,
	appDescriptionAtom,
	appDeveloperAtom,
	appNameAtom,
	appPlatformLinksAtom,
} from '../../../contentFormSections'
import { TAppButton } from '../../../contentFormSections/AppForm/AppInfoFormModel'
import {
	AmazonButton,
	ApplestoreButton,
	MiappButton,
	PlaystoreButton,
} from '../../../contentFormSections/AppForm/Icons'
import {
	imageFormAtom,
	imageFormFileAtom,
} from '../../../contentFormSections/ImageForm'
import { QrScreen, TQrScreenProps } from '../../../QrScreen/QrScreen'
import { useColoredButton } from '../../hooks/useColoredButton'
import { appPrimaryColorAtom, appSecondaryColorAtom } from './appScreenState'

const Header = ({ demoMode }: TQrScreenProps) => {
	const t = useTranslations()
	const name = useAtomValue(appNameAtom)
	const developer = useAtomValue(appDeveloperAtom)
	const description = useAtomValue(appDescriptionAtom)
	return (
		<div className="flex flex-col items-center px-5 pb-5 pt-4 transition-colors word-break-fix">
			<h1 className="type-heading-4 text-center transition-colors">
				{name || (demoMode && t('generator__app_screen__name_fallback'))}
			</h1>
			<h2 className="leading-4 mb-2 text-center text-[10px] text-dark-grey transition-colors">
				{developer ||
					(demoMode && t('generator__app_screen__developer_fallback'))}
			</h2>
			<p className="leading-4 text-center text-[10px] transition-colors [word-break:break-word]">
				{description ||
					(demoMode && t('generator__app_screen__description_fallback'))}
			</p>
		</div>
	)
}
const CustomButton = ({
	previewMode,
	demoMode,
	button: { title, url },
}: {
	previewMode: boolean
	demoMode: boolean
	button: TAppButton
}) => {
	const t = useTranslations()
	const secondaryColorAtom = useAtomValue(appSecondaryColorAtom)
	const { coloredButtonCls, coloredButtonStyle } =
		useColoredButton(secondaryColorAtom)

	const displayedTitle = demoMode
		? t('generator__facebook_screen__demo_mode_button_placeholder')
		: title

	const fontSize = useFrameFontSize({
		text: title,
		maxWidth: 220,
		maxSize: previewMode || demoMode ? 12 : 15,
		fontFamily: 'var(--font-body)',
	})

	const style = {
		fontSize: `${fontSize}px`,
		...coloredButtonStyle,
	}

	const baseButtonCls = cn(
		buttonCls({ visual: 'secondary' }),
		'w-full grow text-body-caption font-normal',
		'rounded-6',
	)

	return (
		<a
			href={addMissingUrlProtocol(url || '')}
			target="_blank"
			style={style}
			className={cn(
				baseButtonCls,
				coloredButtonCls,
				getVersionForClone({
					base: '',
					clone1: 'rounded-6',
				}),
			)}
			rel="noreferrer"
		>
			{displayedTitle}
		</a>
	)
}

const DemoButton = () => {
	const t = useTranslations()
	const secondaryColorAtom = useAtomValue(appSecondaryColorAtom)
	const { coloredButtonCls, coloredButtonStyle } =
		useColoredButton(secondaryColorAtom)
	return (
		<div
			className={cn(
				buttonCls({ visual: 'secondary' }),
				coloredButtonCls,
				'grow text-body-caption font-normal',
				'rounded-6',
			)}
			style={coloredButtonStyle}
		>
			{t('generator__facebook_screen__demo_mode_button_placeholder')}
		</div>
	)
}

const Buttons = ({
	previewMode,
	demoMode,
}: {
	previewMode: boolean
	demoMode: boolean
}) => {
	const buttonList = useAtomValue(appButtonListAtom)
	const firstButton = buttonList[0]
	const additionalButtonList = buttonList.slice(1)

	return (
		<div className="mx-auto flex max-w-[400px] flex-col gap-2 px-5">
			<div className="flex flex-nowrap gap-2">
				{firstButton ? (
					<CustomButton
						button={firstButton}
						previewMode={previewMode}
						demoMode={demoMode}
					/>
				) : demoMode ? (
					<DemoButton />
				) : null}
			</div>
			{additionalButtonList.map((button, index) => (
				<CustomButton
					key={index}
					button={button}
					previewMode={previewMode}
					demoMode={demoMode}
				/>
			))}
		</div>
	)
}

const PlatformButtons = ({ demoMode }: { demoMode: boolean }) => {
	const platformLinks = useAtomValue(appPlatformLinksAtom)

	const platformIconMap: Record<string, React.ReactNode> = {
		apple: <ApplestoreButton />,
		google: <PlaystoreButton />,
		amazon: <AmazonButton />,
		xiaomi: <MiappButton />,
	}
	if (!platformLinks?.length && !demoMode) return null

	return (
		<div className="mx-auto flex max-w-[400px] flex-col gap-2 p-4 px-5">
			{demoMode
				? Object.keys(platformIconMap).map((platform) => (
						<div
							key={platform}
							className={cn(
								buttonCls({ visual: 'default' }),
								'flex h-12 items-center justify-center bg-black ring-transparent',
								'rounded-6',
							)}
						>
							{platformIconMap[platform]}
						</div>
					))
				: platformLinks.map(({ name, url }) => (
						<a
							key={name}
							href={addMissingUrlProtocol(url || '')}
							target="_blank"
							rel="noopener noreferrer"
							className={cn(
								buttonCls({ visual: 'default' }),
								'flex h-12 items-center justify-center bg-black ring-transparent active:bg-black hover:bg-black',
								'rounded-6',
							)}
						>
							{platformIconMap[name] || name}
						</a>
					))}
		</div>
	)
}

const Content = ({ previewMode, demoMode }: TQrScreenProps) => {
	const primaryColor = useAtomValue(appPrimaryColorAtom)
	const image = useAtomValue(imageFormAtom)
	const imageFile = useAtomValue(imageFormFileAtom)

	const [localPreviewSrc, setLocalPreviewSrc] = useState<string | null>(null)
	useEffect(() => {
		if (!imageFile) {
			setLocalPreviewSrc(null)
			return
		}
		const url = URL.createObjectURL(imageFile)
		setLocalPreviewSrc(url)
		return () => URL.revokeObjectURL(url)
	}, [imageFile])

	const imageSrc = image?.publicId
		? `${image.bucketRootUrl}/${image.publicId}`
		: localPreviewSrc

	const demoImage = (
		<NextImage
			src="/web-app/qr-preview-demos/app/app.svg"
			width={80}
			height={80}
			quality={100}
			loading="lazy"
			alt="demo image"
			className="rounded-[17px] object-cover"
		/>
	)
	const actualImage = imageSrc ? (
		// eslint-disable-next-line @next/next/no-img-element
		<img
			alt={image?.publicId ?? 'uploading'}
			src={imageSrc}
			width={80}
			height={80}
			className="rounded-[20px] object-cover p-1.5"
		/>
	) : (
		demoImage
	)

	return (
		<>
			<div className="pb-6">
				<div className="relative grid place-items-center pt-6">
					<div
						className="absolute top-0 h-[60px] w-full transition-colors"
						style={{ backgroundColor: primaryColor }}
					/>
					<div className="z-10 rounded-[20px] bg-white shadow-generator">
						{actualImage}
					</div>
				</div>
				<Header previewMode={previewMode} demoMode={demoMode} />
				<Buttons demoMode={demoMode} previewMode={previewMode} />
				<PlatformButtons demoMode={demoMode} />
			</div>
		</>
	)
}

export const AppScreen = (props: TQrScreenProps) => (
	<QrScreen
		{...props}
		primaryColor={useAtomValue(appPrimaryColorAtom)}
		content={<Content {...props} />}
	/>
)
